import { useMemo } from 'react';

import moment from 'moment';

import { selectedDate } from '../../../types/interfaces';
import { SortConfig } from '../types';

import { CalendarAssignmentsFiltersState } from './useCalendarAssignmentsTableControls';

export interface CalendarAssignmentsApiParams {
  date: string | undefined;
  name: string | undefined;
  personnelNumber: string | undefined;
  sortBy: 'personnelNumber' | 'name' | undefined;
  sortDirection: 'asc' | 'desc' | undefined;
}

export interface UseCalendarAssignmentsQueryParamsArgs {
  selectedDateFilter: Date | null;
  debouncedFilters: CalendarAssignmentsFiltersState;
  sortConfig: SortConfig;
}

export function useCalendarAssignmentsQueryParams({
  selectedDateFilter,
  debouncedFilters,
  sortConfig
}: UseCalendarAssignmentsQueryParamsArgs) {
  const dateForQuery = useMemo(() => {
    if (!selectedDateFilter) return '';
    const year = selectedDateFilter.getFullYear();
    const month = String(selectedDateFilter.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }, [selectedDateFilter]);

  const monthDateFilterForUnassigned = useMemo<selectedDate>(() => {
    const date = selectedDateFilter ?? new Date();
    return {
      dateFrom: String(moment(date).startOf('month').valueOf()),
      dateUntil: String(moment(date).endOf('month').valueOf())
    };
  }, [selectedDateFilter]);

  const apiParams = useMemo<CalendarAssignmentsApiParams>(
    () => ({
      date: dateForQuery || undefined,
      name: debouncedFilters.name.value || undefined,
      personnelNumber: debouncedFilters.personnelNumber.value || undefined,
      sortBy: sortConfig.sortBy !== 'none' ? sortConfig.sortBy : undefined,
      sortDirection:
        sortConfig.sortDirection !== 'none'
          ? sortConfig.sortDirection
          : undefined
    }),
    [dateForQuery, debouncedFilters, sortConfig]
  );

  return {
    dateForQuery,
    monthDateFilterForUnassigned,
    apiParams
  };
}
